import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  Row,
  Select,
  Skeleton,
  Space,
  Statistic,
  Tabs,
  Tag,
  Typography,
  message,
  theme
} from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import PageHeader from "../shared/PageHeader";
import Amount from "../shared/Amount";
import { formatCents, formatDate } from "../shared/money";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../../design-system/useMediaQuery";
import { isValidIban } from "../settings/constants";
import * as api from "../receivables/creditsApi";
import { branchLabel, branchOfDoc, sameId } from "../../../../commons/Billing/customerBranches";
import StatementDialog from "../reports/StatementDialog";
import {
  CreditSource,
  CreditStatusTag,
  InsolvencyModal,
  OffsetModal,
  PendingRefunds,
  RefundModal,
  isUsable
} from "./CreditDialogs";

// UI-4: a customer's account — balance, open items, credits (offset / refund), the Debitor
// reconciliation, insolvency and the receivables master data (refund IBAN, USt 1 TG).
// Mounted by receivables/Receivables.jsx at /user/accounting/receivables/customers/:id; the id
// can also be passed as a prop.

const OPEN_STATUSES = [
  "sent",
  "partially_paid",
  "overdue",
  "reminded_1",
  "reminded_2",
  "reminded_3",
  "handed_over",
  "partially_written_off"
];

const STATUS_COLOR = {
  sent: "blue",
  partially_paid: "cyan",
  overdue: "gold",
  reminded_1: "orange",
  reminded_2: "volcano",
  reminded_3: "red",
  handed_over: "magenta",
  paid: "green",
  settled: "green",
  written_off: "default",
  partially_written_off: "default",
  cancelled: "default"
};

const TYPE_LABEL = {
  invoice: "Invoice",
  partial: "Partial invoice",
  advance: "Advance invoice",
  final: "Final invoice",
  credit_note: "Correction invoice",
  cancellation: "Cancellation invoice",
  correction_notice: "Correction notice"
};

// Mirrors utils/accounting/invoicing#retainedOf: a Sicherheitseinbehalt not yet due.
const retainedOf = (invoice, today) => {
  const r = invoice.retention || {};
  if (!r.amount || r.released) return 0;
  if (r.dueDate && r.dueDate <= today) return 0;
  return Math.min(Number(r.amount), Number(invoice.amounts?.open || 0));
};

const customerName = c =>
  c?.company || [c?.firstName, c?.lastName].filter(Boolean).join(" ") || c?.email || "—";

// ---------------------------------------------------------------------------------------------

const MasterDataCard = ({ customer, onSaved }) => {
  const { t } = useTranslation("accounting");
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const until = customer?.taxExemptionCertificate?.validUntil;
    form.setFieldsValue({
      refundIban: customer?.refundIban || "",
      refundBic: customer?.refundBic || "",
      validUntil: until ? dayjs(until) : null
    });
  }, [customer, form]);

  const save = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSaving(true);
    try {
      await api.updateReceivablesData(customer._id, {
        refundIban: values.refundIban || "",
        refundBic: values.refundBic || "",
        taxExemptionCertificate: {
          validUntil: values.validUntil ? dayjs(values.validUntil).format("YYYY-MM-DD") : ""
        }
      });
      message.success(t("Saved"));
      onSaved?.();
    } catch (error) {
      message.error(api.errorText(error, t("Could not update the customer")));
    } finally {
      setSaving(false);
    }
  };

  const until = customer?.taxExemptionCertificate?.validUntil;
  const expired = until && until < dayjs().format("YYYY-MM-DD");
  const mandateIban = customer?.sepaMandate?.iban;
  return (
    <Card size="small" title={t("Receivables data")}>
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="refundIban"
              label={t("Customer IBAN for refunds")}
              extra={
                mandateIban
                  ? t("Empty: the IBAN of the SEPA mandate ({{iban}}) is used.", {
                      iban: mandateIban
                    })
                  : null
              }
              normalize={v =>
                String(v || "")
                  .replace(/\s+/g, "")
                  .toUpperCase()
              }
              rules={[
                {
                  validator: (_, v) =>
                    isValidIban(v)
                      ? Promise.resolve()
                      : Promise.reject(new Error(t("Please enter a valid IBAN")))
                }
              ]}
            >
              <Input placeholder="DE00 0000 0000 0000 0000 00" autoComplete="off" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="refundBic" label={t("BIC (optional)")}>
              <Input autoComplete="off" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="validUntil"
              label={t("USt 1 TG certificate valid until")}
              extra={t(
                "Proof that the customer is a construction contractor (§ 13b (5) UStG); reverse charge needs a valid certificate."
              )}
            >
              <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
        </Row>
        {expired ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={t("The USt 1 TG certificate expired on {{date}}", {
              date: formatDate(until)
            })}
          />
        ) : null}
        <Button type="primary" loading={saving} onClick={save}>
          {t("Save")}
        </Button>
      </Form>
    </Card>
  );
};

const ReconciliationCard = ({ reconciliation }) => {
  const { t } = useTranslation("accounting");
  const { token } = theme.useToken();
  const r = reconciliation || {};
  const hasLedger = r.ledgerBalance !== null && r.ledgerBalance !== undefined;
  const off = hasLedger && Number(r.difference || 0) !== 0;
  return (
    <Card size="small" title={t("Reconciliation with the ledger")}>
      {!r.debitorAccount ? (
        <Typography.Text type="secondary">
          {t("No Debitor account yet: the first sent invoice creates it.")}
        </Typography.Text>
      ) : (
        <>
          <Descriptions column={1} size="small">
            <Descriptions.Item label={t("Debitor account")}>
              <span className="acct-num">{r.debitorAccount}</span>
            </Descriptions.Item>
            <Descriptions.Item label={t("Balance in the ledger")}>
              {hasLedger ? <Amount cents={r.ledgerBalance} strong /> : "–"}
            </Descriptions.Item>
            <Descriptions.Item label={t("Expected from open items and credits")}>
              <Amount cents={r.expected} />
            </Descriptions.Item>
            <Descriptions.Item label={t("Difference")}>
              {hasLedger ? (
                <Amount
                  cents={r.difference}
                  strong
                  style={off ? { color: token.colorError } : undefined}
                />
              ) : (
                "–"
              )}
            </Descriptions.Item>
          </Descriptions>
          {off ? (
            <Alert
              type="error"
              showIcon
              message={t("The Debitor account does not match the open items")}
              description={t(
                "Check manual journal entries on this account or ask your tax advisor before closing the period."
              )}
            />
          ) : hasLedger ? (
            <Tag color="success">{t("Ledger and open items match")}</Tag>
          ) : null}
        </>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------------------------

const CustomerAccount = ({ customerId, backTo = "/user/accounting/receivables" }) => {
  const { t } = useTranslation("accounting");
  const params = useParams();
  const id = customerId || params.id;
  const history = useHistory();
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [offsetOf, setOffsetOf] = useState(null);
  const [refundOf, setRefundOf] = useState(null);
  const [insolvencyOpen, setInsolvencyOpen] = useState(false);
  // "all" | "none" (head office, no branch) | branch id — filters the tables, not the totals
  const [branchFilter, setBranchFilter] = useState("all");
  const [statementOpen, setStatementOpen] = useState(false);
  const todayIso = dayjs().format("YYYY-MM-DD");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.customerAccount(id);
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(api.errorText(err, t("Could not load the customer account")));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  const customer = data?.customer;
  const branches = customer?.branches || [];
  const allInvoices = useMemo(() => data?.invoices || [], [data]);
  const invoices = useMemo(
    () =>
      branchFilter === "all"
        ? allInvoices
        : allInvoices.filter(i =>
            branchFilter === "none" ? !i.clientBranch : sameId(i.clientBranch, branchFilter)
          ),
    [allInvoices, branchFilter]
  );
  const branchOf = row => branchLabel(branchOfDoc({ ...row, client: customer }));
  const credits = useMemo(() => data?.credits || [], [data]);

  // All open items of the customer (totals, offsets); `openRows` is the branch-filtered view
  const allOpenRows = useMemo(
    () =>
      allInvoices
        .filter(i => OPEN_STATUSES.includes(i.status) && Number(i.amounts?.open || 0) > 0)
        .map(i => {
          const retention = retainedOf(i, todayIso);
          return {
            ...i,
            invoiceNumber: i.invoiceNumber || i.title,
            open: Number(i.amounts?.open || 0) - retention,
            fullOpen: Number(i.amounts?.open || 0),
            retention,
            retentionDueDate: retention ? i.retention?.dueDate || "" : ""
          };
        }),
    [allInvoices, todayIso]
  );
  const openRows = useMemo(() => {
    const visible = new Set(invoices.map(i => String(i._id)));
    return allOpenRows.filter(i => visible.has(String(i._id)));
  }, [allOpenRows, invoices]);
  const offsetTargets = allOpenRows.map(i => ({
    _id: i._id,
    invoiceNumber: i.invoiceNumber,
    open: i.fullOpen
  }));
  const retentionTotal = allOpenRows.reduce((s, i) => s + i.retention, 0);
  const totals = data?.totals || {};
  const balance = (totals.open || 0) + (totals.charges || 0) - (totals.credits || 0);

  if (error)
    return (
      <Alert
        type="error"
        showIcon
        message={error}
        action={<Button onClick={load}>{t("Retry")}</Button>}
      />
    );
  if (!data) return <Skeleton active paragraph={{ rows: 8 }} />;

  const openInvoice = row => history.push(`/invoices/details/${row._id}`);
  const insolvent = Boolean(customer?.insolvency?.date);

  const statusTag = s => <Tag color={STATUS_COLOR[s] || "default"}>{t(`status.${s}`, s)}</Tag>;

  const openColumns = [
    {
      title: t("Invoice number"),
      dataIndex: "invoiceNumber",
      mobile: "title",
      render: (v, row) => <a onClick={() => openInvoice(row)}>{v}</a>
    },
    {
      title: t("Open"),
      key: "open-mobile",
      dataIndex: "open",
      mobile: "subtitle",
      hidden: !isMobile,
      render: v => <Amount cents={v} large />
    },
    { title: t("Status"), dataIndex: "status", render: statusTag },
    {
      title: t("Branch"),
      key: "branch",
      hidden: !branches.length,
      render: (_, r) => branchOf(r) || t("Head office")
    },
    {
      title: t("Due date"),
      dataIndex: "dueDate",
      render: v => <span className="acct-num">{formatDate(v)}</span>
    },
    {
      title: t("Gross"),
      key: "gross",
      align: "end",
      render: (_, r) => <Amount cents={r.amounts?.gross} />
    },
    {
      title: t("Retention"),
      dataIndex: "retention",
      align: "end",
      render: (v, row) =>
        v ? (
          <Space direction="vertical" size={0} style={{ alignItems: "flex-end" }}>
            <Amount cents={v} />
            {row.retentionDueDate ? (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t("due {{date}}", { date: formatDate(row.retentionDueDate) })}
              </Typography.Text>
            ) : null}
          </Space>
        ) : (
          "–"
        )
    },
    {
      title: t("Open"),
      dataIndex: "open",
      align: "end",
      hidden: isMobile,
      render: v => <Amount cents={v} strong />
    }
  ];

  const creditColumns = [
    {
      title: t("Source"),
      key: "source",
      mobile: "title",
      render: (_, c) => (
        <Space direction="vertical" size={0}>
          <CreditSource credit={c} />
          <Typography.Text type="secondary" style={{ fontSize: 12 }} className="acct-num">
            {formatDate(c.date)}
          </Typography.Text>
        </Space>
      )
    },
    {
      title: t("Remaining"),
      key: "remaining-mobile",
      mobile: "subtitle",
      hidden: !isMobile,
      render: (_, c) => <Amount cents={-c.remaining} large signed />
    },
    { title: t("Status"), dataIndex: "status", render: s => <CreditStatusTag status={s} /> },
    { title: t("Amount"), dataIndex: "amount", align: "end", render: v => <Amount cents={v} /> },
    {
      title: t("Remaining"),
      dataIndex: "remaining",
      align: "end",
      hidden: isMobile,
      render: v => <Amount cents={-v} strong signed />
    },
    {
      title: t("Used for"),
      key: "usages",
      render: (_, c) =>
        c.usages?.length ? (
          <Space direction="vertical" size={0} className="acct-wrap">
            {c.usages.map((u, i) => (
              <Typography.Text key={u._id || i} style={{ fontSize: 12 }}>
                {formatDate(u.date)} · {u.kind === "offset" ? t("Offset") : t("Refund")}{" "}
                {u.invoice?.invoiceNumber || ""} · {formatCents(u.amount)}
                {u.pending ? ` (${t("pending")})` : ""}
              </Typography.Text>
            ))}
            <PendingRefunds credit={c} />
          </Space>
        ) : (
          "–"
        )
    },
    {
      title: "",
      key: "actions",
      render: (_, c) =>
        isUsable(c) ? (
          <div className="acct-actions">
            <Button size="small" onClick={() => setOffsetOf(c)} disabled={!offsetTargets.length}>
              {t("Offset")}
            </Button>
            <Button size="small" onClick={() => setRefundOf(c)}>
              {t("Refund")}
            </Button>
          </div>
        ) : null
    }
  ];

  const allColumns = [
    {
      title: t("Number"),
      key: "number",
      mobile: "title",
      render: (_, r) => <a onClick={() => openInvoice(r)}>{r.invoiceNumber || r.title}</a>
    },
    { title: t("Status"), dataIndex: "status", render: statusTag },
    { title: t("Type"), dataIndex: "type", render: v => t(TYPE_LABEL[v] || "Invoice") },
    {
      title: t("Branch"),
      key: "branch",
      hidden: !branches.length,
      render: (_, r) => branchOf(r) || t("Head office")
    },
    {
      title: t("Invoice date"),
      dataIndex: "issueDate",
      render: v => <span className="acct-num">{formatDate(v)}</span>
    },
    {
      title: t("Gross"),
      key: "gross",
      align: "end",
      mobile: "subtitle",
      render: (_, r) => (
        <Amount
          cents={
            ["credit_note", "cancellation"].includes(r.type)
              ? -Math.abs(r.amounts?.gross || 0)
              : r.amounts?.gross
          }
        />
      )
    },
    {
      title: t("Open"),
      key: "open",
      align: "end",
      render: (_, r) => (r.amounts?.open ? <Amount cents={r.amounts.open} /> : "–")
    }
  ];

  const kpis = [
    { key: "open", title: t("Open invoices"), value: totals.open || 0 },
    { key: "retention", title: t("Of which retention"), value: retentionTotal },
    { key: "charges", title: t("Dunning charges"), value: totals.charges || 0 },
    { key: "credits", title: t("Credits"), value: -(totals.credits || 0) },
    { key: "balance", title: t("Balance"), value: balance }
  ];

  return (
    <div>
      <PageHeader
        title={customerName(customer)}
        subtitle={
          <Space wrap size={4}>
            {customer?.customerNumber ? (
              <span>
                {t("Customer number")} {customer.customerNumber}
              </span>
            ) : null}
            {insolvent ? <Tag color="red">{t("Insolvent")}</Tag> : null}
          </Space>
        }
        extra={
          <>
            <Button onClick={() => history.push(backTo)}>{t("Back")}</Button>
            <Button onClick={() => setStatementOpen(true)}>
              {branchFilter === "all" ? t("Account statement") : t("Branch statement")}
            </Button>
            <Button onClick={load} loading={loading}>
              {t("Refresh")}
            </Button>
            {!insolvent ? (
              <Button danger onClick={() => setInsolvencyOpen(true)}>
                {t("Record insolvency")}
              </Button>
            ) : null}
          </>
        }
      />
      {insolvent ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={t("Insolvency proceedings opened on {{date}}", {
            date: formatDate(customer.insolvency.date)
          })}
          description={[customer.insolvency.court, customer.insolvency.fileNo]
            .filter(Boolean)
            .join(" · ")}
        />
      ) : null}
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        {kpis.map(k => (
          <Col xs={12} sm={8} md={k.key === "balance" ? 8 : 4} key={k.key}>
            <Card size="small" className="acct-kpi">
              <Statistic title={k.title} value={formatCents(k.value)} />
            </Card>
          </Col>
        ))}
      </Row>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card size="small">
            {branches.length ? (
              <Select
                style={{ minWidth: 220, marginBottom: 8 }}
                value={branchFilter}
                onChange={setBranchFilter}
                options={[
                  { value: "all", label: t("All branches") },
                  { value: "none", label: t("Head office (no branch)") },
                  ...branches.map(b => ({
                    value: String(b._id),
                    label: `${branchLabel(b)}${b.active === false ? ` · ${t("Archived")}` : ""}`
                  }))
                ]}
              />
            ) : null}
            <Tabs
              items={[
                {
                  key: "open",
                  label: `${t("Open items")} (${openRows.length})`,
                  children: (
                    <ResponsiveTable
                      rowKey="_id"
                      size="small"
                      loading={loading}
                      pagination={false}
                      dataSource={openRows}
                      columns={openColumns}
                      locale={{ emptyText: t("No open items") }}
                    />
                  )
                },
                {
                  key: "credits",
                  label: `${t("Credits")} (${credits.filter(isUsable).length})`,
                  children: (
                    <ResponsiveTable
                      rowKey="_id"
                      size="small"
                      loading={loading}
                      pagination={false}
                      dataSource={credits}
                      columns={creditColumns}
                      locale={{ emptyText: t("No credits") }}
                    />
                  )
                },
                {
                  key: "all",
                  label: t("All documents"),
                  children: (
                    <ResponsiveTable
                      rowKey="_id"
                      size="small"
                      loading={loading}
                      pagination={{ pageSize: 20, hideOnSinglePage: true }}
                      dataSource={invoices}
                      columns={allColumns}
                    />
                  )
                }
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <ReconciliationCard reconciliation={data.reconciliation} />
            <MasterDataCard customer={customer} onSaved={load} />
          </Space>
        </Col>
      </Row>
      <StatementDialog
        open={statementOpen}
        onClose={() => setStatementOpen(false)}
        customerId={customer?._id}
        branch={branchFilter === "all" ? "" : branchFilter}
      />
      <OffsetModal
        open={Boolean(offsetOf)}
        credit={offsetOf}
        invoices={offsetTargets}
        onClose={() => setOffsetOf(null)}
        onDone={load}
      />
      <RefundModal
        open={Boolean(refundOf)}
        credit={refundOf}
        customerId={customer?._id}
        iban={customer?.refundIban || customer?.sepaMandate?.iban}
        onClose={() => setRefundOf(null)}
        onDone={load}
      />
      <InsolvencyModal
        open={insolvencyOpen}
        customer={customer}
        openInvoices={openRows.map(i => ({ ...i, open: i.fullOpen }))}
        onClose={() => setInsolvencyOpen(false)}
        onDone={load}
      />
    </div>
  );
};

export default CustomerAccount;
